import React, { useEffect, useMemo } from 'react';
import { BsTrash } from 'react-icons/bs';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Box,
  Divider, Flex,
  Icon, IconButton,
  Text
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { hoverStyle } from '../../../../../components/MultiSelectDropdown/styles';
import LoadingSpinner from '../../../../../components/Spinner';
import {
  getCompanyDepartments,
  selectCompanyDepartments,
  selectCompanyDepartmentsPageInfo
} from '../../../../../redux/modules/company';
import {
  getDepartmentEmailTemplates, getDepartmentSignatureTemplates,
  selectCurrentDepartment,
  selectDepartmentEmailTemplates, selectDepartmentSignatureTemplates,
  setCurrentBodyTemplateName,
  setCurrentMessageHtml, setCurrentSignature, setCurrentSignatureTemplateName,
  setCurrentSubjectHtml,
  setSelectedCheckboxCodes
} from '../../../../../redux/modules/emails';
import { showModal } from '../../../../../redux/modules/modal';
import CompanyDepartmentsDropdown from '../CompanyDepartmentsDropdown';
import ConfirmDeleteEmailTemplate from '../ConfirmDeleteEmailTemplate';

function DepartmentEmailTemplates({ type, templateWord, ...props }) {
  const dispatch = useDispatch();
  const { companyId } = useParams();
  const companyDepartments = useSelector(selectCompanyDepartments);
  const companyDepartmentsPageInfo = useSelector(selectCompanyDepartmentsPageInfo);
  const departmentEmailTemplates = useSelector(selectDepartmentEmailTemplates);
  const departmentSignatureTemplates = useSelector(selectDepartmentSignatureTemplates);
  const selectedDepartment = useSelector(selectCurrentDepartment);

  const dataSet = useMemo(
    () => (type === 'main_content' ? departmentEmailTemplates.data : departmentSignatureTemplates.data),
    [departmentEmailTemplates, departmentSignatureTemplates, type]
  );

  useEffect(() => {
    if (!companyDepartments?.length) dispatch(getCompanyDepartments(companyId));
  }, [companyId, dispatch]); // eslint-disable-line

  const onDepartmentSelect = dept => {
    if (type === 'main_content') dispatch(getDepartmentEmailTemplates(companyId, dept.departmentId));
    if (type === 'signature') dispatch(getDepartmentSignatureTemplates(companyId, dept.departmentId));
  };

  const fetchMoreData = () => {
    if (type === 'main_content') {
      const pageNumber = departmentEmailTemplates.currentPage + 1;
      dispatch(getDepartmentEmailTemplates(
        companyId,
        selectedDepartment.departmentId,
        { pageSize: 10, pageNumber }
      ));
    } else {
      const pageNumber = departmentSignatureTemplates.currentPage + 1;
      dispatch(getDepartmentSignatureTemplates(
        companyId,
        selectedDepartment.departmentId,
        { pageSize: 10, pageNumber }
      ));
    }
  };

  const onTemplateSelect = template => {
    if (template.templateType === 'main_content') {
      dispatch(setCurrentBodyTemplateName(template.templateName));
      dispatch(setCurrentMessageHtml(template.templateInfo.html));
      dispatch(setCurrentSubjectHtml(template.templateInfo.subjectHtml));
      if (template.templateInfo.codeLetters) {
        dispatch(setSelectedCheckboxCodes(template.templateInfo.codeLetters));
      }
    } else if (template.templateType === 'signature') {
      dispatch(setCurrentSignature(template.templateInfo.html));
      dispatch(setCurrentSignatureTemplateName(template.templateName));
    }
  };

  const openConfirmDeleteModal = template => {
    dispatch(showModal(() => (
      <ConfirmDeleteEmailTemplate
        companyId={companyId}
        template={template}
        type="department"
      />
    ), {
      actionRequired: true,
      buttons: {},
      center: true,
      size: 'sm',
      title: 'Delete Template?'
    }));
  };

  if (!companyDepartments?.length || !companyDepartmentsPageInfo?.currentPage) {
    return (
      <Box w={props?.width || '350px'}>
        <LoadingSpinner isInline />
      </Box>
    );
  }

  return (
    <Box {...props}>

      <Flex alignItems="center" flexFlow="row nowrap" h="50px" justifyContent="space-between">
        <Text textStyle="bodyMd600">
          {`Department ${templateWord}s`}
        </Text>

        <CompanyDepartmentsDropdown onSelect={dept => onDepartmentSelect(dept)} />
      </Flex>
      <Divider />

      <Box className="departmentTemplateScroll" minW="300px" my={2}>
        <InfiniteScroll
          dataLength={departmentEmailTemplates.data.length}
          hasMore={departmentEmailTemplates.currentPage !== departmentEmailTemplates.lastPage}
          height="400px"
          loader={(<LoadingSpinner isInline />)}
          next={fetchMoreData}
          scrollableTarget="departmentTemplateScroll"
          scrollThreshold={0.8}
        >
          {dataSet.map(temp => (
            <Flex
              _hover={hoverStyle}
              cursor="pointer"
              flexFlow="row nowrap"
              justifyContent="space-between"
              key={temp.templateId}
              p={2}
              textStyle="bodyMd500"
            >
              <Box flexBasis="90%" textAlign="left" onClick={() => onTemplateSelect(temp)}>
                <Text>{temp.templateName}</Text>
              </Box>
              <Box flexBasis="10%">
                <IconButton
                  icon={<Icon as={BsTrash} />}
                  size="sm"
                  variant="textBlack"
                  onClick={() => openConfirmDeleteModal(temp)}
                />
              </Box>
            </Flex>
          ))}

          {!dataSet?.length && (<Text>{`No ${templateWord}s`}</Text>)}
        </InfiniteScroll>
      </Box>

    </Box>
  );
}

DepartmentEmailTemplates.propTypes = {
  type: PropTypes.oneOf(['main_content', 'signature']).isRequired,
  templateWord: PropTypes.string
};

export default DepartmentEmailTemplates;
